import * as React from "react";
import { StatusRejectionCause } from "./Report";
import ReportStatus from "../commons/model/report/ReportStatus";
import PatientReportStatus from "../commons/model/report/PatientReportStatus";

namespace ReportStatusUtils {
  interface RenderedStatus {
    status: ReportStatus | PatientReportStatus;
    rejectionCause?: StatusRejectionCause;
    technicalErrorMsg?: string;
  }

  export function getStatusComponent(item: RenderedStatus) {
    return (
      <div className="h-flex status no-wrap">
        <i className={getStatusIcon(item.status)} />
        <div className="v-flex">
          <span>{i18n[getStatusTypeI18nKey(item.status)]}</span>
          {getStatusInfoComponent(item)}
        </div>
      </div>
    );
  }

  function getStatusInfoComponent(item: RenderedStatus) {
    switch (item.status) {
      case ReportStatus.REJECTED:
      case PatientReportStatus.REJECTED:
        return (
          <span className="rejection-cause">
            {i18n[getRejectionCauseI18nKey(item.rejectionCause)]}
          </span>
        );
      case ReportStatus.TECHNICAL_ERROR:
      case PatientReportStatus.TECHNICAL_ERROR:
        return item.technicalErrorMsg ? (
          <span className="technical-error-cause">{item.technicalErrorMsg}</span>
        ) : null;
    }
  }

  export const getStatusIcon = function (status?: ReportStatus | PatientReportStatus) {
    switch (status) {
      case ReportStatus.UPLOADING:
      case PatientReportStatus.UPLOADING:
        return "ic_upload";
      case ReportStatus.ANALYSING:
      case PatientReportStatus.ANALYSING:
        return "ic_analysing";
      case ReportStatus.AVAILABLE:
      case PatientReportStatus.AVAILABLE:
        return "ic_check_circled";
      case ReportStatus.SHARED_WITH_PATIENT:
      case PatientReportStatus.SHARED_WITH_PATIENT:
        return "ic_shared";
      case ReportStatus.VIEWED_BY_PATIENT:
      case PatientReportStatus.VIEWED_BY_PATIENT:
        return "ic_viewed";
      case PatientReportStatus.NO_REPORT:
        return "ic_none";
      case ReportStatus.REJECTED:
      case PatientReportStatus.REJECTED:
      case ReportStatus.INCOMPLETE:
      case PatientReportStatus.INCOMPLETE:
      case ReportStatus.TECHNICAL_ERROR:
      case PatientReportStatus.TECHNICAL_ERROR:
        return "ic_exclamation_circled";
      default:
        return "ic_question";
    }
  };

  export const getStatusTypeI18nKey = function (status: ReportStatus | PatientReportStatus) {
    switch (status) {
      case ReportStatus.UPLOADING:
      case PatientReportStatus.UPLOADING:
        return "Global.Status.uploading";
      case ReportStatus.ANALYSING:
      case PatientReportStatus.ANALYSING:
        return "Global.Status.analysing";
      case ReportStatus.AVAILABLE:
      case PatientReportStatus.AVAILABLE:
        return "Global.Status.available";
      case ReportStatus.SHARED_WITH_PATIENT:
      case PatientReportStatus.SHARED_WITH_PATIENT:
        return "Global.Status.shared_with_patient";
      case ReportStatus.VIEWED_BY_PATIENT:
      case PatientReportStatus.VIEWED_BY_PATIENT:
        return "Global.Status.viewed_by_patient";
      case ReportStatus.REJECTED:
      case PatientReportStatus.REJECTED:
        return "Global.Status.rejected";
      case ReportStatus.INCOMPLETE:
      case PatientReportStatus.INCOMPLETE:
        return "Global.Status.incomplete";
      case ReportStatus.TECHNICAL_ERROR:
      case PatientReportStatus.TECHNICAL_ERROR:
        return "Global.Status.technical_error";
      case PatientReportStatus.NO_REPORT:
        return "Global.Status.no_report";
    }
  };

  export const getRejectionCauseI18nKey = function (statusRejectionCause: string) {
    switch (statusRejectionCause) {
      case StatusRejectionCause.INCORRECT_VIEWS:
        return "Global.Status.rejected_IncorrectViews";
      case StatusRejectionCause.MISSING_VIEWS:
        return "Global.Status.rejected_MissingViews";
      case StatusRejectionCause.POOR_QUALITY:
        return "Global.Status.rejected_PoorQuality";
      case StatusRejectionCause.APPLIANCES_NOT_REMOVED:
        return "Global.Status.rejected_AppliancesNotRemoved";
      case StatusRejectionCause.HIDDEN_TEETH:
        return "Global.Status.rejected_HiddenTeeth";
      case StatusRejectionCause.PHOTO_WITHOUT_TEETH:
        return "Global.Status.rejected_PhotoWithoutTeeth";
      case StatusRejectionCause.UNKNOWN:
        return "Global.Status.rejected_Unknown";
    }
  };

  export function getStatusTypeStyle(s: ReportStatus) {
    switch (s) {
      case ReportStatus.UPLOADING:
      case ReportStatus.ANALYSING:
        return "warning";
      case ReportStatus.AVAILABLE:
      case ReportStatus.SHARED_WITH_PATIENT:
      case ReportStatus.VIEWED_BY_PATIENT:
        return "success";
      case ReportStatus.REJECTED:
      case ReportStatus.INCOMPLETE:
      case ReportStatus.TECHNICAL_ERROR:
        return "danger";
    }
  }
}

export default ReportStatusUtils;
